"use client";

import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { AnimatePresence, motion, type Transition } from "framer-motion";

import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";
import { ArrowDown01Icon } from "@hugeicons/core-free-icons";

/**
 * Animate UI — Radix Accordion.
 *
 * Drop-in replacement for the shadcn accordion: same exports, same trigger
 * markup/classes and chevron, but the content opens/closes with a spring
 * height animation (framer-motion) instead of CSS keyframes.
 *
 * Each item exposes its open state through context; the trigger mirrors Radix's
 * `data-state` into that context via a MutationObserver so the content can drive
 * its enter/exit animation.
 */

type AccordionItemContextType = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const AccordionItemContext = React.createContext<
  AccordionItemContextType | undefined
>(undefined);

const useAccordionItem = (): AccordionItemContextType => {
  const context = React.useContext(AccordionItemContext);
  if (!context) {
    throw new Error("useAccordionItem must be used within an AccordionItem");
  }
  return context;
};

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 150,
  damping: 22,
};

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <AccordionItemContext.Provider value={{ isOpen, setIsOpen }}>
      <AccordionPrimitive.Item
        data-slot="accordion-item"
        className={cn("transition-all", className)}
        {...props}
      >
        {children}
      </AccordionPrimitive.Item>
    </AccordionItemContext.Provider>
  );
}

function AccordionTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const { setIsOpen } = useAccordionItem();

  React.useEffect(() => {
    const node = triggerRef.current;
    if (!node) return;

    const sync = () => setIsOpen(node.getAttribute("data-state") === "open");
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(node, {
      attributes: true,
      attributeFilter: ["data-state"],
    });
    return () => observer.disconnect();
  }, [setIsOpen]);

  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        ref={triggerRef}
        data-slot="accordion-trigger"
        className={cn(
          "flex flex-1 items-center cursor-pointer justify-between gap-4 py-4 text-left text-sm font-medium outline-none disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
          className,
        )}
        {...props}
      >
        {children}
        <Icon icon={ArrowDown01Icon}
          strokeWidth={1.5}
          className="pointer-events-none size-6 shrink-0 translate-y-0.5  data-[state=open]:text-primary  transition-transform duration-200"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({
  className,
  children,
  transition = DEFAULT_TRANSITION,
  keepMounted = false,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & {
  transition?: Transition;
  /**
   * Держать содержимое в разметке даже в закрытом состоянии.
   *
   * По умолчанию закрытая секция не рендерится вовсе — для FAQ это нормально.
   * Но на странице рациона под аккордеонами лежит всё меню, ради которого
   * страница и существует: если его нет в HTML, поисковик не увидит ни одного
   * названия блюда. Здесь содержимое остаётся в DOM и просто схлопнуто.
   */
  keepMounted?: boolean;
}) {
  const { isOpen } = useAccordionItem();

  if (keepMounted) {
    return (
      <AccordionPrimitive.Content
        forceMount
        // Radix вешает hidden на закрытую секцию — тогда её не видно даже
        // при height:auto. Возвращаем отображение и схлопываем сами.
        className="[&[hidden]]:block"
        {...props}
      >
        <motion.div
          data-slot="accordion-content"
          initial={false}
          animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
          transition={transition}
          style={{ overflow: "hidden" }}
        >
          <div className={cn("text-sm", className)}>{children}</div>
        </motion.div>
      </AccordionPrimitive.Content>
    );
  }

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <AccordionPrimitive.Content key="accordion-content" forceMount {...props}>
          <motion.div
            data-slot="accordion-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transition}
            style={{ overflow: "hidden" }}
          >
            <div className={cn("text-sm", className)}>{children}</div>
          </motion.div>
        </AccordionPrimitive.Content>
      )}
    </AnimatePresence>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
